import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Store, Users, DollarSign, Briefcase, Send, CheckCircle2, Clock, AlertTriangle,
  RefreshCw, Plus, Search, ExternalLink, Star, MessageSquare, ChevronRight,
  UserPlus, Target, Zap, ArrowUpRight, Package, ShieldAlert
} from 'lucide-react';

const STATUS_BADGE = {
  open: 'bg-blue-100 text-blue-700', matched: 'bg-purple-100 text-purple-700',
  assigned: 'bg-yellow-100 text-yellow-700', in_progress: 'bg-amber-100 text-amber-700',
  review: 'bg-orange-100 text-orange-700', completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const URGENCY_BADGE = { normal: 'bg-gray-100 text-gray-600', urgent: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };

const TABS = ['Dashboard', 'Service Requests', 'Freelancers', 'Payments', 'External Platforms', 'Partners'];

export default function Marketplace() {
  const { user } = useAuth();
  const [sidebarTab, setSidebarTab] = useState('Marketplace');
  const [tab, setTab] = useState('Dashboard');
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [freelancers, setFreelancers] = useState([]);
  const [platforms, setPlatforms] = useState(null);
  const [partners, setPartners] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [newReq, setNewReq] = useState({ title: '', description: '', category: 'it_support', budget: '', urgency: 'normal', skills_required: '' });
  const [msgText, setMsgText] = useState('');

  const isAdmin = user?.role === 'platform_admin' || user?.role === 'business_admin';

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get('/api/marketplace/stats'); setStats(data); } catch (e) { console.error('Marketplace stats error:', e); }
  }, [isAdmin]);

  const fetchRequests = useCallback(async () => {
    try { const { data } = await api.get('/api/marketplace/service-requests?limit=50'); setRequests(data.requests || []); } catch (e) { console.error('Marketplace requests error:', e); } finally { setLoading(false); }
  }, []);

  const fetchFreelancers = useCallback(async () => {
    try { const { data } = await api.get('/api/talent/freelancer/profiles'); setFreelancers(data.profiles || []); } catch (e) { console.error('Freelancer fetch error:', e); }
  }, []);

  const fetchPlatforms = useCallback(async () => {
    try { const { data } = await api.get('/api/marketplace/external-platforms'); setPlatforms(data); } catch (e) { console.error('Platforms fetch error:', e); }
  }, []);

  const fetchPartners = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get('/api/marketplace/partners'); setPartners(data.partners || []); } catch (e) { console.error('Partners fetch error:', e); }
  }, [isAdmin]);

  const fetchPayments = useCallback(async () => {
    try {
      const [txRes, invRes] = await Promise.all([
        api.get('/api/marketplace/transactions?limit=30'),
        api.get('/api/marketplace/invoices?limit=30'),
      ]);
      setTransactions(txRes.data.transactions || []);
      setInvoices(invRes.data.invoices || []);
    } catch (e) { console.error('Payments fetch error:', e); }
  }, []);

  useEffect(() => { fetchStats(); fetchRequests(); }, [fetchStats, fetchRequests]);
  useEffect(() => { if (tab === 'Freelancers') fetchFreelancers(); }, [tab, fetchFreelancers]);
  useEffect(() => { if (tab === 'External Platforms') fetchPlatforms(); }, [tab, fetchPlatforms]);
  useEffect(() => { if (tab === 'Partners') fetchPartners(); }, [tab, fetchPartners]);
  useEffect(() => { if (tab === 'Payments') fetchPayments(); }, [tab, fetchPayments]);

  const createRequest = async () => {
    if (!newReq.title) { toast.error('Title required'); return; }
    try {
      await api.post('/api/marketplace/service-requests', {
        ...newReq,
        budget: parseFloat(newReq.budget) || 0,
        skills_required: newReq.skills_required ? newReq.skills_required.split(',').map(s => s.trim().toLowerCase()) : [],
      });
      toast.success('Service request created');
      setShowNewRequest(false);
      setNewReq({ title: '', description: '', category: 'it_support', budget: '', urgency: 'normal', skills_required: '' });
      fetchRequests();
      fetchStats();
    } catch { toast.error('Failed to create request'); }
  };

  const matchFreelancers = async (id) => {
    try {
      const { data } = await api.post(`/api/marketplace/service-requests/${id}/match`);
      toast.success(`Found ${data.total_internal} internal matches`);
      fetchRequests();
      if (selectedReq?.id === id) {
        const { data: sr } = await api.get(`/api/marketplace/service-requests/${id}`);
        setSelectedReq(sr);
      }
    } catch { toast.error('Matching failed'); }
  };

  const assignFreelancer = async (requestId, freelancerId, name, source) => {
    try {
      await api.post(`/api/marketplace/service-requests/${requestId}/assign`, { freelancer_id: freelancerId, freelancer_name: name, source });
      toast.success(`Assigned ${name}`);
      fetchRequests();
      if (selectedReq) { const { data } = await api.get(`/api/marketplace/service-requests/${requestId}`); setSelectedReq(data); }
    } catch { toast.error('Assignment failed'); }
  };

  const sendMessage = async (requestId) => {
    if (!msgText.trim()) return;
    try {
      await api.post(`/api/marketplace/service-requests/${requestId}/messages`, { content: msgText });
      setMsgText('');
      const { data } = await api.get(`/api/marketplace/service-requests/${requestId}`);
      setSelectedReq(data);
    } catch { toast.error('Failed'); }
  };

  const initiatePayment = async (requestId) => {
    try {
      const { data } = await api.post('/api/marketplace/checkout', { request_id: requestId, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch { toast.error('Payment initiation failed'); }
  };

  const generateInvoice = async (requestId) => {
    try {
      const { data } = await api.post(`/api/marketplace/invoices/generate/${requestId}`);
      toast.success(`Invoice ${data.invoice_number} generated`);
      fetchPayments();
    } catch { toast.error('Invoice generation failed'); }
  };

  // Check for Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const { data } = await api.get(`/api/marketplace/checkout/status/${sessionId}`);
          if (data.payment_status === 'paid') { toast.success('Payment successful!'); fetchRequests(); fetchStats(); return; }
          if (data.status === 'expired') { toast.error('Payment expired'); return; }
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } catch { /* retry */ }
      };
      pollStatus();
      window.history.replaceState({}, '', '/marketplace');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={sidebarTab} onTabChange={setSidebarTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="marketplace">
      <Sidebar activeTab={sidebarTab} onTabChange={setSidebarTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Service Marketplace</h1>
              <p className="text-sm text-gray-500 mt-1">Manage service requests, freelancers, and external platform referrals</p>
            </div>
            <Button onClick={() => setShowNewRequest(!showNewRequest)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="new-request-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Request
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="marketplace-tabs">
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${tab === t ? 'bg-white text-[#D4A843] shadow-sm' : 'text-gray-500 hover:text-gray-900'}`} data-testid={`tab-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                {t}
              </button>
            ))}
          </div>

          {/* New Request Form */}
          {showNewRequest && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="new-request-form">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Create Service Request</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-2"><Label className="text-xs">Title *</Label><Input value={newReq.title} onChange={e => setNewReq({...newReq, title: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g., Penetration test for cloud infrastructure" data-testid="req-title" /></div>
                <div><Label className="text-xs">Budget ($)</Label><Input type="number" value={newReq.budget} onChange={e => setNewReq({...newReq, budget: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g., 500" data-testid="req-budget" /></div>
                <div><Label className="text-xs">Category</Label>
                  <select value={newReq.category} onChange={e => setNewReq({...newReq, category: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="req-category">
                    <option value="it_support">IT Support & Help Desk</option>
                    <option value="cybersecurity">Cybersecurity & Threat Hunting</option>
                    <option value="scada_ot">SCADA / OT Security</option>
                    <option value="cloud_migration">Cloud Migration</option>
                    <option value="hipaa_compliance">HIPAA & Compliance</option>
                    <option value="web_dev">Web Development</option>
                    <option value="managed_services">Managed IT Services</option>
                  </select>
                </div>
                <div><Label className="text-xs">Urgency</Label>
                  <select value={newReq.urgency} onChange={e => setNewReq({...newReq, urgency: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="req-urgency">
                    <option value="normal">Normal</option><option value="urgent">Urgent</option><option value="critical">Critical</option>
                  </select>
                </div>
                <div><Label className="text-xs">Required Skills (comma-separated)</Label><Input value={newReq.skills_required} onChange={e => setNewReq({...newReq, skills_required: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g., aws, python, siem" data-testid="req-skills" /></div>
                <div className="lg:col-span-3"><Label className="text-xs">Description</Label><textarea value={newReq.description} onChange={e => setNewReq({...newReq, description: e.target.value})} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-xs min-h-[60px]" placeholder="Describe the work needed..." data-testid="req-description" /></div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={createRequest} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="req-submit">Create Request</Button>
                <Button variant="outline" onClick={() => setShowNewRequest(false)} className="text-xs rounded-lg">Cancel</Button>
              </div>
            </div>
          )}

          {/* ═══ DASHBOARD TAB ═══ */}
          {tab === 'Dashboard' && stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift"><Store className="w-5 h-5 text-[#D4A843] mb-2" /><p className="font-heading text-2xl font-bold text-gray-900">{stats.requests?.total || 0}</p><p className="text-xs text-gray-500">Total Requests</p></div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 hover-lift"><Clock className="w-5 h-5 text-blue-600 mb-2" /><p className="font-heading text-2xl font-bold text-blue-700">{stats.requests?.open || 0}</p><p className="text-xs text-blue-600">Open</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 hover-lift"><Briefcase className="w-5 h-5 text-amber-600 mb-2" /><p className="font-heading text-2xl font-bold text-amber-700">{stats.requests?.assigned || 0}</p><p className="text-xs text-amber-600">In Progress</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 hover-lift"><CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" /><p className="font-heading text-2xl font-bold text-emerald-700">{stats.requests?.completed || 0}</p><p className="text-xs text-emerald-600">Completed</p></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <DollarSign className="w-5 h-5 text-[#D4A843] mb-2" />
                  <p className="text-2xl font-bold text-gray-900">${(stats.revenue?.total || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Revenue</p>
                  <div className="flex gap-4 mt-2">
                    <span className="text-[10px] text-emerald-600">Platform: ${(stats.revenue?.platform_earnings || 0).toLocaleString()} ({stats.revenue?.platform_cut_pct}%)</span>
                    <span className="text-[10px] text-gray-400">Freelancers: ${(stats.revenue?.freelancer_payouts || 0).toLocaleString()}</span>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <Users className="w-5 h-5 text-[#7C3AED] mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{stats.freelancers?.total || 0}</p>
                  <p className="text-xs text-gray-500">Freelancers</p>
                  <div className="flex gap-4 mt-2">
                    <span className="text-[10px] text-emerald-600">Verified: {stats.freelancers?.verified || 0}</span>
                    <span className="text-[10px] text-[#D4A843]">Available: {stats.freelancers?.available || 0}</span>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <Target className="w-5 h-5 text-[#B91C1C] mb-2" />
                  <p className="text-xs font-bold text-gray-900 uppercase mb-2">Fiverr Affiliate Links</p>
                  <div className="space-y-1">
                    {Object.entries(stats.fiverr_links || {}).slice(0, 4).map(([k, v]) => (
                      <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-[#D4A843] hover:underline">
                        <ExternalLink className="w-3 h-3" /> Fiverr {k} <ArrowUpRight className="w-2.5 h-2.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {tab === 'Dashboard' && !stats && !isAdmin && (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Store className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Create a service request to get started.</p>
            </div>
          )}

          {/* ═══ SERVICE REQUESTS TAB ═══ */}
          {tab === 'Service Requests' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3" data-testid="request-list">
                {requests.length > 0 ? requests.map(r => (
                  <div key={r.id} onClick={() => setSelectedReq(r)} className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:border-[#D4A843] ${selectedReq?.id === r.id ? 'border-[#D4A843] ring-1 ring-[#D4A843]' : 'border-gray-200'}`} data-testid={`request-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-xs font-bold text-gray-900 line-clamp-1">{r.title}</h4>
                      <div className="flex gap-1.5">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_BADGE[r.status] || ''}`}>{r.status.replace('_', ' ')}</span>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${URGENCY_BADGE[r.urgency] || ''}`}>{r.urgency}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 line-clamp-1">{r.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                      <span>${r.budget}</span>
                      <span>{r.category.replace('_', ' ')}</span>
                      {r.assigned_freelancer_name && <span className="text-emerald-600">Assigned: {r.assigned_freelancer_name}</span>}
                      {r.freelancer_source && <span className="text-[#D4A843]">via {r.freelancer_source}</span>}
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                )) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No service requests yet.</p>
                  </div>
                )}
              </div>

              {/* Detail Panel */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-6" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }} data-testid="request-detail">
                {selectedReq ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">{selectedReq.title}</h3>
                      <div className="flex gap-1.5 mt-1">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_BADGE[selectedReq.status]}`}>{selectedReq.status.replace('_', ' ')}</span>
                        <span className="text-[10px] text-gray-400">${selectedReq.budget} | {selectedReq.category.replace('_', ' ')}</span>
                      </div>
                      <p className="text-[11px] text-gray-600 mt-2 leading-relaxed">{selectedReq.description}</p>
                      <p className="text-[10px] text-gray-400 mt-1">Client: {selectedReq.client_name} | {selectedReq.client_email}</p>
                      {selectedReq.freelancer_payout > 0 && <p className="text-[10px] text-emerald-600 mt-1">Freelancer payout: ${selectedReq.freelancer_payout} (70%)</p>}
                      {!selectedReq.payment_status && selectedReq.budget > 0 && (
                        <Button size="sm" onClick={() => initiatePayment(selectedReq.id)} className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg" data-testid="pay-btn">
                          <DollarSign className="w-3.5 h-3.5 mr-1" /> Pay ${selectedReq.budget} via Stripe
                        </Button>
                      )}
                      {selectedReq.payment_status === 'paid' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-bold">PAID</span>
                          {!selectedReq.invoice_id && isAdmin && (
                            <Button size="sm" variant="outline" className="text-[10px] h-6 rounded" onClick={() => generateInvoice(selectedReq.id)} data-testid="invoice-btn">Generate Invoice</Button>
                          )}
                          {selectedReq.invoice_number && <span className="text-[10px] text-gray-400">Invoice: {selectedReq.invoice_number}</span>}
                        </div>
                      )}
                    </div>

                    {/* Admin Actions */}
                    {isAdmin && selectedReq.status === 'open' && (
                      <Button size="sm" onClick={() => matchFreelancers(selectedReq.id)} className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs rounded-lg" data-testid="match-btn">
                        <Search className="w-3.5 h-3.5 mr-1" /> Find Matching Freelancers
                      </Button>
                    )}

                    {/* Matches */}
                    {selectedReq.matches?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Internal Matches</h4>
                        {selectedReq.matches.map(m => (
                          <div key={m.freelancer_id} className="flex items-center justify-between p-2 bg-gray-50 rounded mb-1">
                            <div>
                              <p className="text-xs font-medium text-gray-900">{m.freelancer_name}</p>
                              <p className="text-[9px] text-gray-400">{m.skills_matched.join(', ')} | Score: {m.match_score}%</p>
                            </div>
                            <Button size="sm" variant="outline" className="text-[10px] h-6 rounded" onClick={() => assignFreelancer(selectedReq.id, m.freelancer_id, m.freelancer_name, 'internal')} data-testid={`assign-${m.freelancer_id}`}>Assign</Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* External Suggestions */}
                    {selectedReq.external_suggestions?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">External Platforms</h4>
                        {selectedReq.external_suggestions.map((s, i) => (
                          <a key={`item-${i}`} href={s.link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-2 bg-[#D4A843]/5 border border-[#D4A843]/20 rounded mb-1 hover:bg-[#D4A843]/10">
                            <div>
                              <p className="text-xs font-medium text-gray-900">{s.platform}</p>
                              <p className="text-[9px] text-gray-400">{s.suggestion}</p>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-[#D4A843]" />
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Assignment info */}
                    {selectedReq.assigned_freelancer_name && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-xs font-bold text-emerald-700">Assigned: {selectedReq.assigned_freelancer_name}</p>
                        <p className="text-[10px] text-emerald-600">Source: {selectedReq.freelancer_source} | Since: {selectedReq.assigned_at ? new Date(selectedReq.assigned_at).toLocaleString() : '—'}</p>
                      </div>
                    )}

                    {/* Deliverables */}
                    {selectedReq.deliverables?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Deliverables</h4>
                        {selectedReq.deliverables.map(d => (
                          <div key={d.id} className="p-2 bg-gray-50 rounded mb-1">
                            <p className="text-xs font-medium text-gray-900">{d.title}</p>
                            <p className="text-[10px] text-gray-400">{d.description}</p>
                            {d.link && <a href={d.link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D4A843] hover:underline">{d.link}</a>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Messages */}
                    <div>
                      <h4 className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Messages ({(selectedReq.messages || []).length})</h4>
                      <div className="space-y-1.5 max-h-[150px] overflow-y-auto mb-2">
                        {(selectedReq.messages || []).map(m => (
                          <div key={m.id} className="p-2 bg-gray-50 rounded">
                            <p className="text-[10px] text-gray-400">{m.sender_name} | {new Date(m.sent_at).toLocaleString()}</p>
                            <p className="text-xs text-gray-800">{m.content}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <Input value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendMessage(selectedReq.id); }} placeholder="Type a message..." className="flex-1 text-xs rounded-lg" data-testid="msg-input" />
                        <Button size="sm" onClick={() => sendMessage(selectedReq.id)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg" data-testid="msg-send"><Send className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Select a request to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ FREELANCERS TAB ═══ */}
          {tab === 'Freelancers' && (
            <div className="space-y-3" data-testid="freelancer-list">
              {freelancers.length > 0 ? freelancers.map(f => (
                <div key={f.user_id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`freelancer-${f.user_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-gray-900">{f.name || f.user_id?.slice(0, 8)}</h4>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${f.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>{f.status}</span>
                        {f.available !== false && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Available</span>}
                      </div>
                      <div className="flex gap-3 mt-1 text-[10px] text-gray-500">
                        {f.hourly_rate && <span>${f.hourly_rate}/hr</span>}
                        {f.experience_years && <span>{f.experience_years}yr exp</span>}
                        {f.completed_jobs && <span>{f.completed_jobs} jobs</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(f.ratings || []).length > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-[#D4A843]">
                          <Star className="w-3.5 h-3.5 fill-[#D4A843]" />
                          {(f.ratings.reduce((a, r) => a + r.score, 0) / f.ratings.length).toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  {f.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {f.skills.slice(0, 8).map(s => <span key={s} className="text-[9px] px-2 py-0.5 bg-gray-100 rounded text-gray-600">{s}</span>)}
                    </div>
                  )}
                </div>
              )) : (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No freelancers onboarded yet. Freelancers can register via Talent Hub.</p>
                </div>
              )}
            </div>
          )}

          {/* ═══ PAYMENTS TAB ═══ */}
          {tab === 'Payments' && (
            <div className="space-y-6">
              {/* Transactions */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="transactions-list">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#D4A843]" /> Payment Transactions</h3>
                {transactions.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {transactions.map(tx => (
                      <div key={tx.id} className={`flex items-center justify-between p-3 rounded-lg ${tx.payment_status === 'paid' ? 'bg-emerald-50' : 'bg-gray-50'}`} data-testid={`tx-${tx.id}`}>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{tx.metadata?.title || tx.request_id?.slice(0, 8)}</p>
                          <p className="text-[10px] text-gray-400">{tx.user_email} | {new Date(tx.created_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-bold text-gray-900">${tx.amount}</p>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${tx.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>{tx.payment_status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-400">No transactions yet.</p>}
              </div>
              {/* Invoices */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="invoices-list">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-[#7C3AED]" /> Invoices</h3>
                {invoices.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50" data-testid={`inv-${inv.id}`}>
                        <div>
                          <p className="text-xs font-bold text-gray-900">{inv.invoice_number}</p>
                          <p className="text-[10px] text-gray-500">{inv.title} | {inv.client_name}</p>
                          <p className="text-[10px] text-gray-400">Issued: {new Date(inv.issued_at).toLocaleDateString()} | Due: {new Date(inv.due_date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-bold text-gray-900">${inv.total}</p>
                          <p className="text-[9px] text-emerald-600">Platform: ${inv.platform_fee} ({inv.platform_fee_pct}%)</p>
                          <p className="text-[9px] text-gray-400">Freelancer: ${inv.freelancer_payout}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-400">No invoices yet. Complete a service request to generate one.</p>}
              </div>
            </div>
          )}

          {/* ═══ EXTERNAL PLATFORMS TAB ═══ */}
          {tab === 'External Platforms' && platforms && (
            <div className="space-y-4">
              {platforms.platforms?.map(p => (
                <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`platform-${p.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">{p.name}</h3>
                      <p className="text-[11px] text-gray-500">{p.description}</p>
                      <p className="text-[10px] text-[#D4A843] mt-1">{p.commission}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-300" />
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {p.best_for?.map(c => <span key={c} className="text-[9px] px-2 py-0.5 bg-[#D4A843]/10 text-[#D4A843] rounded">{c.replace('_', ' ')}</span>)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(p.links || {}).map(([k, v]) => (
                      <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 p-2 bg-gray-50 rounded-lg hover:bg-[#D4A843]/10 transition-colors" data-testid={`link-${p.id}-${k}`}>
                        <ExternalLink className="w-3 h-3 text-[#D4A843]" />
                        <span className="text-xs text-gray-700">{k}</span>
                        <ArrowUpRight className="w-2.5 h-2.5 text-gray-400 ml-auto" />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-2">Service Categories</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {platforms.service_categories?.map(c => (
                    <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-bold text-gray-900">{c.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">{c.skills?.map(s => <span key={s} className="text-[8px] px-1.5 py-0.5 bg-white rounded text-gray-500">{s}</span>)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══ PARTNERS TAB ═══ */}
          {tab === 'Partners' && (
            <div className="space-y-4">
              {/* Tier Structure */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="tier-info">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Commission Tiers</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { name: 'Bronze', rate: '10%', min: '0-5', color: 'bg-orange-100 border-orange-300 text-orange-800' },
                    { name: 'Silver', rate: '12%', min: '6-20', color: 'bg-gray-100 border-gray-300 text-gray-700' },
                    { name: 'Gold', rate: '15%', min: '21-50', color: 'bg-[#D4A843]/20 border-[#D4A843] text-[#D4A843]' },
                    { name: 'Platinum', rate: '20%', min: '51+', color: 'bg-purple-100 border-purple-300 text-purple-700' },
                  ].map(t => (
                    <div key={t.name} className={`p-4 rounded-lg border ${t.color}`}>
                      <p className="text-xs font-bold">{t.name}</p>
                      <p className="text-xl font-bold">{t.rate}</p>
                      <p className="text-[10px] opacity-70">{t.min} referrals</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partners List */}
              <div className="space-y-3" data-testid="partner-list">
                {partners.length > 0 ? partners.map(p => (
                  <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`partner-${p.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">{p.business_name}</h4>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.status}</span>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.tier === 'platinum' ? 'bg-purple-100 text-purple-700' : p.tier === 'gold' ? 'bg-[#D4A843]/20 text-[#D4A843]' : p.tier === 'silver' ? 'bg-gray-200 text-gray-700' : 'bg-orange-100 text-orange-700'}`}>{p.tier || 'bronze'}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">{p.contact_name} | {p.email} | Code: <span className="font-mono font-bold text-[#D4A843]">{p.referral_code}</span></p>
                      </div>
                      {isAdmin && p.status === 'pending' && (
                        <Button size="sm" variant="outline" className="text-[10px] h-6 rounded" onClick={async () => { await api.put(`/api/marketplace/partners/${p.id}`, { status: 'active' }); fetchPartners(); toast.success('Partner activated'); }}>Activate</Button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      <div className="bg-gray-50 rounded p-2 text-center">
                        <p className="text-xs font-bold text-gray-900">{p.total_referrals || 0}</p>
                        <p className="text-[9px] text-gray-400">Referrals</p>
                      </div>
                      <div className="bg-gray-50 rounded p-2 text-center">
                        <p className="text-xs font-bold text-gray-900">${(p.total_earnings || 0).toFixed(2)}</p>
                        <p className="text-[9px] text-gray-400">Total Earned</p>
                      </div>
                      <div className="bg-yellow-50 rounded p-2 text-center">
                        <p className="text-xs font-bold text-yellow-700">${(p.pending_payout || 0).toFixed(2)}</p>
                        <p className="text-[9px] text-gray-400">Pending</p>
                      </div>
                      <div className="bg-emerald-50 rounded p-2 text-center">
                        <p className="text-xs font-bold text-emerald-700">${(p.total_paid_out || 0).toFixed(2)}</p>
                        <p className="text-[9px] text-gray-400">Paid Out</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-[#D4A843] mt-2">Commission rate: {((p.commission_rate || 0.10) * 100).toFixed(0)}% of platform earnings</p>
                    {isAdmin && (p.pending_payout || 0) > 0 && (
                      <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] rounded" onClick={async () => {
                        try {
                          const { data } = await api.post('/api/marketplace/payouts/trigger', { partner_id: p.id, method: 'manual' });
                          toast.success(data.message);
                          fetchPartners();
                        } catch { toast.error('Payout failed'); }
                      }} data-testid={`payout-${p.id}`}>
                        <DollarSign className="w-3 h-3 mr-1" /> Pay Out ${(p.pending_payout || 0).toFixed(2)}
                      </Button>
                    )}
                  </div>
                )) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <UserPlus className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No partners registered yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
